// #region Imports

import React from 'react';

// Material-UI
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

// Styles
import { useTheme } from '@mui/material/styles';

// Components
import PhoneTypeIcon from './PhoneTypeIcon';

import {
  API_URL,
  EMPTY_STRING,
  formatGenderAge
} from '../global';

import {
  HTTPHeaders
} from "../interfaces";

import {
  Member
} from '../models';

// Auth0 user hooks 
import { useAuth0User } from '../hooks/UseAuth0User';

// UTC to local date formatting hooks
import { useDates } from '../hooks/UseDates';

// #endregion

// #region Interfaces

interface MemberViewProps {
  memberID: number;
}

// #endregion

const MemberView: React.FC<MemberViewProps> = (props) => {

  const { getAccessToken } = useAuth0User();
  const { dateStringToLocalDateFormatted } = useDates();

  const [member, setMember] = React.useState<Member>(new Member());

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      flexGrow: 1
    },
    rowSpacing: {
      marginTop: theme.spacing(1)
    },
    updated: {
      marginTop: theme.spacing(2)
    }
  };

  // #endregion

  // #region Methods

  /*
* Fetch member
*/
  const fetchMember = async (id: number) => {

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    await fetch(API_URL + `/v1/members/${id}`, {
      method: 'GET'
      , cache: 'no-cache'
      , headers: headers
      , mode: 'cors'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Member not saved');
        }
        return response.json()
      })
      .then((m: Member) => {
        setMember(m);
      })
      .catch(err => {
        throw new Error('Member not saved');
      });

  }

  // #endregion

  // #region Event handlers

  React.useEffect(() => {
    if (props.memberID > 0) {
      fetchMember(props.memberID);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.memberID]);


  // #endregion

  return (
    <Box my={4} sx={styles.root}>
      {(member.firstName || member.lastName) &&
        <Grid container size={12} sx={styles.rowSpacing}>
          <Typography variant="body1">{`${member.firstName} ${member.lastName} (${member.memberStatus.status})`}</Typography>
        </Grid>
      }
      {member.email &&
        <Grid container size={12} sx={styles.rowSpacing} >
          <Typography variant="body1">{member.email}</Typography>
        </Grid>
      }
      {(member.primaryPhone) &&
        <Grid container size={12} sx={styles.rowSpacing}>
          <Link href={'tel:' + member.primaryPhone} variant="body1" underline="none">{member.primaryPhone}</Link><span>&nbsp;</span><PhoneTypeIcon phoneType={member.primaryPhoneType} />
        </Grid>
      }
      {(member.secondaryPhone) &&
        <Grid container size={12} sx={styles.rowSpacing}>
          <Link href={'tel:' + member.secondaryPhone} variant="body1" underline="none">{member.secondaryPhone}</Link><span>&nbsp;</span><PhoneTypeIcon phoneType={member.secondaryPhoneType} />
        </Grid>
      }
      {(member.gender || member.age) &&
        <Grid container size={12} sx={styles.rowSpacing} >
          <Typography variant="body1">{formatGenderAge(member.gender, member.age)}</Typography>
        </Grid>
      }
      <Grid container size={12} sx={styles.updated}>
        <Typography variant="overline">Updated: {dateStringToLocalDateFormatted(member.updated)} {member.updatedBy ? member.updatedBy : EMPTY_STRING}</Typography>
      </Grid>
    </Box>
  );
}

export default MemberView;
